<script setup lang="ts">
import type {ProjectData} from "~/types/ProjectData";
import type {ChapterData} from "~/types/ChapterData";

const props = defineProps<{
  project: ProjectData,
  chapter?: ChapterData
}>();

const toast = useToast();
const {refreshIdentity} = useSanctumAuth();
const selectedAmount = ref<number | null>(null);
const isConfirming = ref(false);
const isSuccess = ref(false);
const isLoading = ref(false);

function selectAmount(amount: number) {
  selectedAmount.value = amount;
  isConfirming.value = true;
  isSuccess.value = false;
}

function cancel() {
  selectedAmount.value = null;
  isConfirming.value = false;
}

async function confirmDonation() {
  if (!selectedAmount.value || !props.project.authors[0]) return;

  isLoading.value = true;
  try {
    await $api("/api/donations", {
      method: "POST",
      body: {
        amount: selectedAmount.value,
        project_uuid: props.project.uuid,
        recipient_uuid: props.project.authors[0].uuid,
        chapter_uuid: props.chapter?.uuid,
      },
    });
    await refreshIdentity();
    isSuccess.value = true;
    isConfirming.value = false;
    selectedAmount.value = null;
  } catch (error: any) {
    toast.add({
      title: "Erreur",
      description: error.data?.message || "Une erreur est survenue",
      color: "error",
    });
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="bg-gradient-scribs p-6">
    <h3 class="text-h5 underline mb-4">Soutenir cette histoire</h3>
    <div class="text-gray-700 mb-4">
      <p v-if="project.scribs_donors_count && project.scribs_donors_count > 0">
        <span class="font-semibold">{{ project.scribs_donors_count }}</span> lectrices et lecteurs soutiennent cette histoire.
      </p>
      <p v-else>
        Soyez le premier à soutenir cette histoire !
      </p>
    </div>
    <div class="grid grid-cols-2 gap-4">
      <UButton
          color="secondary"
          variant="solid"
          class="justify-center"
          :disabled="isLoading"
          @click="selectAmount(5)"
          icon="i-custom-coin"
      >
        5 scribs
      </UButton>
      <UButton
          color="secondary"
          variant="solid"
          class="justify-center"
          :disabled="isLoading"
          @click="selectAmount(10)"
          icon="i-custom-coin"
      >
        10 scribs
      </UButton>
      <UButton
          color="secondary"
          variant="solid"
          class="justify-center"
          :disabled="isLoading"
          @click="selectAmount(25)"
          icon="i-custom-coin"
      >
        25 scribs
      </UButton>
      <UButton
          color="secondary"
          variant="solid"
          class="justify-center"
          :disabled="isLoading"
          @click="selectAmount(50)"
          icon="i-custom-coin"
      >
        50 scribs
      </UButton>
    </div>

    <div v-if="isConfirming || isSuccess" class="mt-4 p-4 rounded-sm bg-white border-2">
      <div v-if="isConfirming">
        <p class="font-semibold">Merci pour ton soutien !</p>
        <p class="mb-4">Tu es sur le point de soutenir cette histoire avec {{ selectedAmount }} scribs.</p>
        <div class="flex gap-2">
          <UButton
              color="primary"
              variant="solid"
              :loading="isLoading"
              @click="confirmDonation"
          >
            Confirmer
          </UButton>
          <UButton
              :disabled="isLoading"
              @click="cancel"
          >
            Annuler
          </UButton>
        </div>
      </div>

      <div v-if="isSuccess">
        <p class="font-semibold">C'est envoyé !</p>
        <p>Merci pour ton soutien ! Nous en informons l'auteur.e.</p>
      </div>
    </div>
  </div>
</template>
